import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Capture a DOM node (the plan viewer container) and export as annotated PDF.
 */
export async function exportPlanAsPdf(node, filename = "plan-annotated.pdf") {
  if (!node) return;
  const canvas = await html2canvas(node, { useCORS: true, backgroundColor: "#ffffff", scale: 2, logging: false });
  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
  const w = canvas.width * ratio;
  const h = canvas.height * ratio;
  pdf.addImage(imgData, "PNG", (pageW - w) / 2, (pageH - h) / 2, w, h);
  pdf.save(filename);
}

/**
 * Export a claim report as a well-formatted PDF (metadata + description + comments).
 */
export function exportClaimReport(claim, asset, comments = []) {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const M = 40;
  let y = M;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text("Rapport de sinistre", M, y); y += 25;
  pdf.setFontSize(10);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(120);
  pdf.text(`Généré le ${new Date().toLocaleString("fr-FR")}`, M, y); y += 25;

  pdf.setTextColor(0);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(14);
  pdf.text(claim.title || "—", M, y); y += 18;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`Référence : ${claim.reference}`, M, y); y += 14;
  pdf.text(`Famille : ${claim.claim_family === "DO" ? "Dommages-Ouvrage" : "Multirisque"}   Statut : ${claim.status}`, M, y); y += 14;
  pdf.text(`Actif : ${asset?.name || "—"}`, M, y); y += 14;
  pdf.text(`Assureur : ${claim.insurer || "—"}   Courtier : ${claim.broker || "—"}   Expert : ${claim.expert || "—"}`, M, y); y += 14;
  pdf.text(`Survenance : ${claim.date_of_occurrence || "—"}   Déclaré : ${claim.date_reported || "—"}`, M, y); y += 20;

  // Financial
  pdf.setFont("helvetica", "bold"); pdf.text("Financier", M, y); y += 14;
  pdf.setFont("helvetica", "normal");
  const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
  pdf.text(`Montant estimé : ${eur(claim.estimated_damage_amount)}`, M, y); y += 14;
  pdf.text(`Évaluation expert : ${eur(claim.expert_assessed_amount)}`, M, y); y += 14;
  pdf.text(`Indemnisation : ${eur(claim.compensation_amount)}`, M, y); y += 14;
  pdf.text(`Reste à charge : ${eur(claim.remaining_cost)}`, M, y); y += 20;

  // Description
  pdf.setFont("helvetica", "bold"); pdf.text("Description", M, y); y += 14;
  pdf.setFont("helvetica", "normal");
  const desc = pdf.splitTextToSize(claim.description || "—", 515);
  pdf.text(desc, M, y); y += desc.length * 12 + 10;

  if (claim.next_action) {
    pdf.setFont("helvetica", "bold"); pdf.text("Prochaine action", M, y); y += 14;
    pdf.setFont("helvetica", "normal");
    const na = pdf.splitTextToSize(claim.next_action, 515);
    pdf.text(na, M, y); y += na.length * 12 + 10;
  }

  // Comments
  if (comments.length) {
    pdf.setFont("helvetica", "bold"); pdf.text(`Commentaires (${comments.length})`, M, y); y += 14;
    pdf.setFont("helvetica", "normal");
    for (const c of comments) {
      if (y > 780) { pdf.addPage(); y = M; }
      pdf.setTextColor(120); pdf.setFontSize(9);
      pdf.text(`${c.created_by_name || "—"} · ${new Date(c.created_at).toLocaleString("fr-FR")}`, M, y); y += 11;
      pdf.setTextColor(0); pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(c.body, 515);
      pdf.text(lines, M, y); y += lines.length * 12 + 6;
    }
  }

  pdf.save(`${claim.reference}-rapport.pdf`);
}
